import { useState } from "react";
import { Loader2, PlugZap, Search, X, ChevronDown, ChevronUp, Users, ReceiptText, Wallet, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/api";
import { newRef } from "@/offline";
import { Modal } from "@/components/Dialogs";
import { paymentStatus } from "@/lib/paymentStatus";
import { parsePeriods } from "@/lib/periods";

const num = (v) => { const n = parseInt(String(v ?? "").replace(/[^\d-]/g, ""), 10); return Number.isFinite(n) ? n : 0; };
const rupiah = (n) => `Rp ${(n || 0).toLocaleString("id-ID")}`;
const todayStr = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };

// Form Entry Pembayaran (Smart Pay Tahap 3) — menyimpan ke customer_payments lokal MITRAnet.
function PaymentForm({ item, billing, onClose, onDone }) {
  const raw = (billing?.raw || item.raw || {});
  const cust = item.raw || {};
  const [ref] = useState(() => newRef()); // client_ref tetap untuk mencegah double-submit ganda
  // Periode HANYA dari data e-Billing (periode_belum_lunas); tidak ada input teks bebas.
  const [periods] = useState(() => parsePeriods(billing?.raw || {}));
  const [sel, setSel] = useState(() => (periods.length === 1 && periods[0].valid && !periods[0].lunas ? { 0: true } : {}));
  const [noms, setNoms] = useState(() => Object.fromEntries(periods.map((p, i) => [i, p.nominal ? String(p.nominal) : ""])));
  const due = String(raw.tgl_jatuh_tempo || "").slice(0, 10);
  const [method, setMethod] = useState("cash");
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(null); // [{label, ok, data|error}]
  const nTunggak = num(raw.jumlah_periode_belum_lunas);
  const chosen = periods.map((p, i) => ({ ...p, i, nom: num(noms[i]) })).filter((p) => sel[p.i]);
  const total = chosen.reduce((s, p) => s + p.nom, 0);
  const canSave = chosen.length > 0 && chosen.every((p) => p.valid && !p.lunas && p.nom > 0);

  const submit = async (e) => {
    e.preventDefault();
    if (!canSave) return toast.error("Pilih minimal 1 periode valid dan isi nominal setiap periode");
    setBusy(true);
    const results = [];
    // 1 transaksi per periode (alokasi jelas). client_ref per periode -> retry aman, tanpa duplikat.
    for (const p of chosen) {
      try {
        const { data } = await api.post("/admin/customer-payments", {
          client_ref: `${ref}-${p.label}`,
          customer_id: String(cust.id_pelanggan || raw.id_pelanggan || item.id || ""),
          customer_name: String(cust.nama_warga || cust.nama_pelanggan || raw.nama_warga || raw.nama_pelanggan || item.nama || ""),
          customer_phone: String(cust.no_hp || cust.tlp || raw.no_hp || raw.tlp || item.phone || ""),
          nama_langganan: String(raw.nama_langganan || cust.nama_langganan || ""),
          periode: p.label, nominal_tagihan: p.nom, paid_amount: p.nom,
          tgl_jatuh_tempo: due, payment_method: method, connection_status: "online",
        });
        results.push({ label: p.label, ok: true, data });
      } catch (err) { results.push({ label: p.label, ok: false, error: errMsg(err, "Gagal") }); }
    }
    setBusy(false);
    if (results.some((r) => r.ok)) { setSaved(results); onDone?.(results); }
    else toast.error(`Pembayaran TIDAK tercatat: ${results.map((r) => `${r.label}: ${r.error}`).join("; ")}`);
  };

  if (saved) {
    return (
      <div data-testid="pay-success">
        <div style={{ textAlign: "center", margin: "8px 0 12px" }}>
          <CheckCircle2 size={40} color="#16a34a" style={{ margin: "0 auto" }} />
          <h3 style={{ margin: "8px 0 2px" }}>Pembayaran Tercatat</h3>
          <p className="muted-sm">Status: <strong>Tercatat lokal di MITRAnet</strong></p>
        </div>
        <div className="detail-grid" style={{ display: "grid", gap: 4, fontSize: 13 }}>
          {saved.map((r, k) => r.ok
            ? <span key={k} data-testid={`pay-result-ok-${k}`}>{r.label}: <strong>{rupiah(r.data.paid_amount)}</strong> · ID <strong data-testid="pay-trx-id">{r.data.id}</strong></span>
            : <span key={k} style={{ color: "#b91c1c" }} data-testid={`pay-result-fail-${k}`}>{r.label}: GAGAL — {r.error}</span>)}
          <span>Total tercatat: <strong>{rupiah(saved.filter((r) => r.ok).reduce((s, r) => s + r.data.paid_amount, 0))}</strong> · Metode: <strong style={{ textTransform: "capitalize" }}>{method}</strong></span>
        </div>
        <p className="dialog-text" style={{ marginTop: 10 }}>Catatan: transaksi ini <strong>tercatat lokal di MITRAnet</strong>. Status di e-Billing <strong>belum berubah</strong> — belum ada konfirmasi resmi dari e-Billing.</p>
        <div className="actions-right">
          <button className="btn-primary" onClick={onClose} data-testid="pay-done">Selesai</button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="pay-form">
      <div className="detail-grid" style={{ display: "grid", gap: 4, fontSize: 13, marginBottom: 10 }}>
        <span>Nama: <strong>{cust.nama_pelanggan || raw.nama_pelanggan || item.nama}</strong></span>
        <span>ID Pelanggan: <strong>{cust.id_pelanggan || raw.id_pelanggan || item.id}</strong></span>
        <span>Paket: <strong>{raw.nama_langganan || cust.nama_langganan || "-"}</strong></span>
        {billing && <span>Status tagihan e-Billing: <strong>{raw.status_pembayaran || "Belum Lunas"}</strong></span>}
        {nTunggak > 1 && <span style={{ color: "#b91c1c" }} data-testid="pay-tunggakan-info">Info: pelanggan memiliki {nTunggak} periode belum lunas (informasi saja — tidak wajib dilunasi sekaligus).</span>}
      </div>
      <label className="field-label">Periode yang dibayar (dari e-Billing)</label>
      {periods.length === 0 && <p className="empty-note" data-testid="pay-no-period">Tidak ada periode belum lunas dari e-Billing untuk pelanggan ini — pembayaran tidak dapat disimpan.</p>}
      {periods.map((p, i) => (
        <div key={i} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6, flexWrap: "wrap" }} data-testid={`pay-period-${i}`}>
          <label style={{ display: "flex", gap: 6, alignItems: "center", minWidth: 170, opacity: p.valid && !p.lunas ? 1 : 0.6 }}>
            <input type="checkbox" disabled={!p.valid || p.lunas} checked={!!sel[i]} onChange={(e) => setSel({ ...sel, [i]: e.target.checked })} data-testid={`pay-period-check-${i}`} />
            {p.valid ? <strong>{p.label}</strong> : <span style={{ color: "#b91c1c" }}>Periode tidak dikenal — periksa Detail</span>}
            {p.lunas && <span className="badge" data-testid={`pay-period-lunas-${i}`}>LUNAS</span>}
          </label>
          {p.valid && !p.lunas && <input className="text-input" style={{ width: 140 }} type="number" min="1" placeholder="Nominal (Rp)" value={noms[i]} onChange={(e) => setNoms({ ...noms, [i]: e.target.value })} data-testid={`pay-period-nominal-${i}`} />}
          {!p.valid && <code className="muted-sm" style={{ wordBreak: "break-all" }}>{p.rawText}</code>}
        </div>
      ))}
      <p className="muted-sm" data-testid="pay-total">Total dibayar: <strong>{rupiah(total)}</strong> ({chosen.length} periode)</p>
      <label className="field-label">Metode pembayaran</label>
      <select className="text-input" value={method} onChange={(e) => setMethod(e.target.value)} data-testid="pay-method">
        <option value="cash">Cash</option>
        <option value="transfer">Transfer</option>
      </select>
      <div className="actions-right" style={{ marginTop: 12 }}>
        <button type="button" className="btn-ghost" onClick={onClose} data-testid="pay-cancel">Batal</button>
        <button type="submit" className="btn-primary" disabled={busy || !canSave} data-testid="pay-submit">
          {busy ? <Loader2 className="spin" size={16} /> : <Wallet size={16} />} Simpan Pembayaran
        </button>
      </div>
    </form>
  );
}

// Menu Pelanggan & Tagihan e-Billing. e-Billing = source of truth.
// Kredensial transient (tidak disimpan), field dirender dinamis (termasuk raw).
export function EbillingCustomers({ storeId, store, mode = "pelanggan" }) {
  const isBilling = mode === "tagihan";
  const q = { params: storeId ? { store_id: storeId } : {} };
  const [creds, setCreds] = useState({ account: "", username: "", password: "" });
  const [connected, setConnected] = useState(false);
  const [items, setItems] = useState(null);
  const [billingItems, setBillingItems] = useState([]); // daftar belum-bayar utk status & entry (mode pelanggan)
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState("");
  const [openRow, setOpenRow] = useState(null);
  const [payTarget, setPayTarget] = useState(null); // { item, billing }
  const set = (k) => (e) => setCreds({ ...creds, [k]: e.target.value });
  const endpoint = isBilling ? "/admin/ebilling/tagihan" : "/admin/ebilling/pelanggan";

  const connect = async (e) => {
    if (e) e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(endpoint, { ...creds, search }, q);
      setItems(data.items || []);
      if (!isBilling) {
        // Ambil daftar belum-bayar SEKALI saat koneksi (password masih tersedia) untuk status & form pembayaran
        try {
          const b = await api.post("/admin/ebilling/tagihan", { ...creds }, q);
          setBillingItems(b.data.items || []);
        } catch { setBillingItems([]); }
      }
      setConnected(true);
      // keamanan: password tidak disimpan di state frontend setelah request selesai
      setCreds((c) => ({ ...c, password: "" }));
      if ((data.items || []).length === 0) toast.info("Tidak ada data dari e-Billing untuk akun ini.");
    } catch (err) { toast.error(errMsg(err, "Gagal mengambil data dari e-Billing.")); }
    finally { setBusy(false); }
  };

  const disconnect = () => { setConnected(false); setItems(null); setBillingItems([]); setCreds({ account: "", username: "", password: "" }); setOpenRow(null); setPayTarget(null); };

  const billingFor = (it) => {
    if (isBilling) return it;
    const cid = String(it.raw?.id_pelanggan || it.id || "");
    return billingItems.find((b) => String(b.raw?.id_pelanggan || b.id || "") === cid) || null;
  };

  if (!connected) {
    return (
      <form onSubmit={connect} data-testid={`ebilling-conn-form-${mode}`} style={{ maxWidth: 460 }}>
        <h3 className="section-title" style={{ marginTop: 0, display: "flex", alignItems: "center", gap: 6 }}>
          {isBilling ? <ReceiptText size={18} /> : <Users size={18} />} {isBilling ? "Tagihan e-Billing" : "Pelanggan e-Billing"}
        </h3>
        <p className="dialog-text">Masukkan akun e-Billing Anda. Password hanya dipakai saat ini untuk mengambil data dan <strong>tidak disimpan</strong> di mana pun.</p>
        <label className="field-label">Kode Akun / Tenant (account)</label>
        <input className="text-input" value={creds.account} onChange={set("account")} required data-testid={`ebc-account-${mode}`} />
        <label className="field-label">Username</label>
        <input className="text-input" value={creds.username} onChange={set("username")} required data-testid={`ebc-username-${mode}`} />
        <label className="field-label">Password</label>
        <input className="text-input" type="password" value={creds.password} onChange={set("password")} required data-testid={`ebc-password-${mode}`} />
        <div className="actions-right" style={{ marginTop: 12 }}>
          <button type="submit" className="btn-primary" disabled={busy} data-testid={`ebc-connect-${mode}`}>
            {busy ? <Loader2 className="spin" size={16} /> : <PlugZap size={16} />} Hubungkan e-Billing
          </button>
        </div>
      </form>
    );
  }

  // Search/filter CLIENT-SIDE pada dataset yang sudah diterima — tanpa login ulang
  // (password sengaja dikosongkan setelah koneksi pertama; request ulang tanpa kredensial = 401).
  const q2 = search.trim().toLowerCase();
  const shown = (items || []).filter((it) => !q2 ||
    `${it.nama || ""} ${it.id || ""} ${it.phone || ""} ${it.alamat || ""} ${it.periode || ""}`.toLowerCase().includes(q2));

  const cols = isBilling
    ? [["id", "ID Pelanggan"], ["nama", "Nama"], ["periode", "Periode"], ["nominal", "Nominal"], ["jatuh_tempo", "Jatuh Tempo"], ["status_langganan", "Status Langganan"]]
    : [["id", "ID"], ["nama", "Nama"], ["phone", "No. HP"], ["alamat", "Alamat"], ["lokasi", "Lokasi"], ["status", "Status"]];

  return (
    <div data-testid={`ebilling-list-${mode}`}>
      <div className="panel-toolbar" style={{ flexWrap: "wrap", gap: 8 }}>
        <span className="muted-sm" data-testid={`ebc-count-${mode}`}>{shown.length}{shown.length !== items.length ? ` dari ${items.length}` : ""} {isBilling ? "tagihan" : "pelanggan"} · sumber e-Billing</span>
        <div className="toolbar-right" style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <div style={{ position: "relative" }}>
            <Search size={14} style={{ position: "absolute", left: 8, top: 9, opacity: 0.5 }} />
            <input className="text-input" style={{ paddingLeft: 28, height: 34 }} placeholder="Cari nama / ID / HP (filter langsung)" value={search}
              onChange={(e) => setSearch(e.target.value)} data-testid={`ebc-search-${mode}`} />
          </div>
          <button className="btn-ghost sm" onClick={disconnect} data-testid={`ebc-disconnect-${mode}`}><X size={14} /> Putus</button>
        </div>
      </div>

      {shown.length === 0 ? <p className="empty-note">{items.length > 0 ? "Tidak ada hasil untuk pencarian ini." : "Tidak ada data."}</p> : (
        <div className="table-wrap" style={{ overflowX: "auto" }}>
          <table className="data-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr>{cols.map(([k, l]) => <th key={k} style={{ textAlign: "left", padding: "6px 8px", borderBottom: "2px solid #e2e8f0" }}>{l}</th>)}<th style={{ textAlign: "left", padding: "6px 8px", borderBottom: "2px solid #e2e8f0" }}>Status Bayar</th><th></th></tr>
            </thead>
            <tbody>
              {shown.map((it, i) => {
                const billing = billingFor(it);
                const st = paymentStatus(billing, it.raw);
                return (
                  <>
                    <tr key={i} data-testid={`ebc-row-${mode}-${i}`} style={{ borderBottom: "1px solid #f1f5f9" }}>
                      {cols.map(([k]) => <td key={k} style={{ padding: "6px 8px" }}>{it[k] || "-"}</td>)}
                      <td style={{ padding: "6px 8px" }}>
                        <span className="badge" style={{ background: st.bg, color: st.color }} data-testid={`ebc-paystatus-${mode}-${i}`}>{st.text}</span>
                        {billing?.raw?.jumlah_periode_belum_lunas != null && num(billing.raw.jumlah_periode_belum_lunas) > 1 && (
                          <div className="muted-sm" data-testid={`ebc-tunggakan-${mode}-${i}`}>{num(billing.raw.jumlah_periode_belum_lunas)} periode belum lunas</div>
                        )}
                      </td>
                      <td style={{ padding: "6px 8px", whiteSpace: "nowrap" }}>
                        <button className="btn-primary sm" onClick={() => setPayTarget({ item: it, billing })} data-testid={`ebc-pay-${mode}-${i}`}>
                          <Wallet size={13} /> Bayar
                        </button>{" "}
                        <button className="btn-ghost sm" onClick={() => setOpenRow(openRow === i ? null : i)} data-testid={`ebc-detail-${mode}-${i}`}>
                          {openRow === i ? <ChevronUp size={13} /> : <ChevronDown size={13} />} Detail
                        </button>
                      </td>
                    </tr>
                    {openRow === i && (
                      <tr key={`${i}-d`}>
                        <td colSpan={cols.length + 2} style={{ background: "#f8fafc", padding: "8px 12px" }} data-testid={`ebc-detail-box-${mode}-${i}`}>
                          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(200px,1fr))", gap: 6 }}>
                            {Object.entries(it.raw || {}).map(([k, v]) => (
                              <div key={k} style={{ fontSize: 12 }}><strong style={{ color: "#475569" }}>{k}:</strong> {v == null || typeof v === "object" ? JSON.stringify(v) : String(v)}</div>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Modal open={!!payTarget} title="Entry Pembayaran Pelanggan" onClose={() => setPayTarget(null)} testId="pay-modal">
        {payTarget && <PaymentForm item={payTarget.item} billing={payTarget.billing} onClose={() => setPayTarget(null)} onDone={() => {}} />}
      </Modal>
    </div>
  );
}
